import type { ContractParty, ContractSignerSummary } from "@closer/shared";
import { Button, FormField, Modal, Select, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { UyeListesiDurumu } from "../../components/UyeListesiDurumu";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";

/**
 * İMZACI PLANI (CLOS-165 `[158]`).
 *
 * ### ⚠ Plan TOPLUCA kaydediliyor
 *
 * Ekranda satırlar eklenip çıkarılıyor ama sunucuya planın SON HÂLİ tek komutta gidiyor.
 * Sıra ve zorunluluk birbirine bağlı kararlar; parça parça gönderilseydi arada tutarsız
 * bir plan (iki kişi aynı sırada, hiç zorunlu imzacı yok) kaydedilebilirdi.
 *
 * ### ⚠ Her imzacı bir TARAFI temsilen imzalıyor
 *
 * Kişi seçimi tek başına yetmiyor; hangi taraf adına imzaladığı da seçiliyor. Kartın
 * kuralı: *"katılımcı rolü tek başına imza yetkisi vermez."* Sunucu ayrıca üyeliği,
 * `contract.sign` yetkisini, kayıt erişimini ve doğrulanmış e-postayı kontrol ediyor —
 * ekran bunları tekrar etmiyor, reddi olduğu gibi gösteriyor.
 */
export function ContractSignerPlanDialog({
  open,
  contractId,
  requestId,
  mevcut,
  onClose,
  onDone,
}: {
  open: boolean;
  contractId: string;
  requestId: string;
  mevcut: ContractSignerSummary[];
  onClose: () => void;
  onDone: () => Promise<void> | void;
}) {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;
  const toast = useToast();

  /**
   * ⚠ `signerTenantId` satırın PARÇASI: sunucu imzacının hangi çalışma alanından
   * imzaladığını istiyor (katılımcı organizasyondan imzacı gelebiliyor). Elle turda
   * atlanmıştı ve plan kaydı 422 döndürdü — testte gövde alan alan ölçülüyor.
   */
  type Satir = {
    partyId: string;
    signerUserId: string;
    signerTenantId: string;
    required: boolean;
  };
  const [satirlar, setSatirlar] = useState<Satir[]>([]);
  const [taraf, setTaraf] = useState("");
  const [kisi, setKisi] = useState("");
  const [zorunlu, setZorunlu] = useState("true");

  const taraflar = useQuery({
    enabled: open,
    queryKey: tenantKey(tenantId, "contract", contractId, "taraflar"),
    queryFn: async () =>
      (
        await api<{ parties: ContractParty[]; missing: string[] }>(
          `/api/contracts/${contractId}/parties`,
          { tenantId },
        )
      ).data,
  });

  /**
   * ADAYLAR TARAF BAŞINA VE SUNUCUDAN (CLOS-1186).
   *
   * ⚠ Eskiden liste `/internal-candidates` ucundan geliyordu ve o uç YALNIZ kendi çalışma
   * alanının aktif üyelerini döndürüyor. Karşı tarafın imzacısı ekrandan HİÇ seçilemiyordu —
   * oysa sunucu kapısı kiracılar arası imzacıyı destekliyor. Kapı doğru, arayüz onu hiç
   * kullanamıyordu.
   *
   * ⚠ Uç KAPIYLA AYNI kuralı konuşuyor (`lib/eligibleActors.ts` → `actorMayActForParty`).
   * İstemcide ikinci bir süzgeç yazmıyoruz: iki kural bir gün ayrışırdı — bu kartın
   * kapattığı kusur tam olarak o ayrışmaydı.
   */
  const tarafKimlikleri = (taraflar.data?.parties ?? []).map((t) => t.id);
  const adaySorgulari = useQueries({
    queries: tarafKimlikleri.map((partyId) => ({
      enabled: open,
      queryKey: tenantKey(tenantId, "contract", contractId, "uygun-aktor", partyId, "sign"),
      queryFn: async () =>
        (
          await api<{ actors: { userId: string; tenantId: string; name: string }[] }>(
            `/api/contracts/${contractId}/parties/${partyId}/eligible-actors?capability=contract.sign`,
            { tenantId },
          )
        ).data.actors,
    })),
  });
  const tarafAdaylari = new Map<string, { userId: string; tenantId: string; name: string }[]>(
    tarafKimlikleri.map((partyId, i) => [partyId, adaySorgulari[i]?.data ?? []]),
  );

  useEffect(() => {
    if (!open) return;
    /** Mevcut plan yükleniyor: düzenleme sıfırdan başlamıyor, üzerine yazıyor. */
    setSatirlar(
      mevcut.map((i) => ({
        partyId: i.partyId,
        signerUserId: i.signerUserId,
        signerTenantId: i.signerTenantId,
        required: i.required,
      })),
    );
    setTaraf("");
    setKisi("");
    setZorunlu("true");
  }, [open, mevcut]);

  /** ⚠ Taraf seçilmeden aday listesi ÇİZİLMİYOR: hangi taraf adına imzalandığı listeyi belirliyor. */
  const aktifUyeler = taraf ? (tarafAdaylari.get(taraf) ?? []) : [];
  const adayHatasi = adaySorgulari.find((q) => q.error)?.error ?? null;

  const kaydet = useMutation({
    mutationFn: async () =>
      api(`/api/contracts/${contractId}/signature-requests/${requestId}/signers`, {
        method: "PUT",
        tenantId,
        body: JSON.stringify({ signers: satirlar }),
      }),
    onSuccess: async () => {
      toast.success("İmzacı planı kaydedildi");
      await onDone();
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "İmzacı planı kaydedilemedi"),
  });

  function ekle() {
    if (!taraf || !kisi) return;
    /**
     * ⚠ KİRACI ADAYDAN OKUNUYOR, çağıranınkinden DEĞİL (CLOS-1186). Sabit `tenantId`
     * yazmak, liste yalnız kendi üyelerini gösterirken doğruydu; kapsam genişleyince
     * sunucu kişiyi yanlış kiracıda arar oldu ve plan 403 alırdı.
     */
    const aday = (tarafAdaylari.get(taraf) ?? []).find((a) => a.userId === kisi);
    if (!aday) return;
    /** Aynı kişi planda bir kez: sunucu da reddediyor, ekran denettirmiyor. */
    if (satirlar.some((s) => s.signerUserId === kisi)) {
      toast.error("Bu kişi planda zaten var");
      return;
    }
    setSatirlar((onceki) => [
      ...onceki,
      {
        partyId: taraf,
        signerUserId: kisi,
        signerTenantId: aday.tenantId,
        required: zorunlu === "true",
      },
    ]);
    setKisi("");
  }

  /** ⚠ Ad TÜM taraf listelerinden aranıyor: satır başka bir tarafın adayını taşıyor olabilir. */
  const ad = (userId: string) =>
    [...tarafAdaylari.values()].flat().find((u) => u.userId === userId)?.name ??
    "Bilinmeyen kullanıcı";
  const tarafAdi = (partyId: string) =>
    (taraflar.data?.parties ?? []).find((p) => p.id === partyId)?.displayName ?? "Bilinmeyen taraf";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="İmzacıları belirle"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Vazgeç
          </Button>
          <Button
            onClick={() => kaydet.mutate()}
            loading={kaydet.isPending}
            disabled={satirlar.length === 0}
          >
            Planı kaydet
          </Button>
        </>
      }
    >
      {taraflar.isPending ? (
        <div role="status" aria-label="Seçenekler yükleniyor">
          <Skeleton lines={3} />
        </div>
      ) : (taraflar.data?.parties ?? []).length === 0 ? (
        <p className="text-sm text-fg-muted">
          İmzacı belirlemek için önce sözleşmeye taraf eklemelisiniz; her imzacı bir tarafı temsilen
          imzalar.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2">
            <FormField label="Taraf">
              <Select
                id="imzaci-taraf"
                value={taraf}
                onChange={(olay) => setTaraf(olay.target.value)}
              >
                <option value="">Seçiniz</option>
                {(taraflar.data?.parties ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.displayName}
                  </option>
                ))}
              </Select>
            </FormField>

            <FormField label="İmzacı">
              <Select id="imzaci-kisi" value={kisi} onChange={(olay) => setKisi(olay.target.value)}>
                <option value="">Seçiniz</option>
                {aktifUyeler.map((u) => (
                  <option key={u.userId} value={u.userId}>
                    {u.name}
                  </option>
                ))}
              </Select>
            </FormField>

            <UyeListesiDurumu hata={adayHatasi} bosMu={taraf !== "" && aktifUyeler.length === 0} />

            <FormField label="Zorunluluk">
              <Select
                id="imzaci-zorunlu"
                value={zorunlu}
                onChange={(olay) => setZorunlu(olay.target.value)}
              >
                <option value="true">Zorunlu imzacı</option>
                <option value="false">Opsiyonel imzacı</option>
              </Select>
            </FormField>

            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={ekle}
              disabled={!taraf || !kisi}
            >
              Listeye ekle
            </Button>
          </div>

          {satirlar.length === 0 ? (
            <p className="text-sm text-fg-muted">Plan boş; en az bir imzacı ekleyin.</p>
          ) : (
            <ol className="flex flex-col gap-1">
              {satirlar.map((s, sira) => (
                <li
                  key={s.signerUserId}
                  className="flex items-center justify-between gap-2 border-b border-border-default py-1.5 last:border-0"
                >
                  <span className="text-sm text-fg">
                    {sira + 1}. {ad(s.signerUserId)}{" "}
                    <span className="text-fg-muted">
                      ({tarafAdi(s.partyId)}
                      {s.required ? "" : " · opsiyonel"})
                    </span>
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      setSatirlar((onceki) =>
                        onceki.filter((x) => x.signerUserId !== s.signerUserId),
                      )
                    }
                  >
                    Çıkar
                  </Button>
                </li>
              ))}
            </ol>
          )}
          <p className="text-xs text-fg-soft">
            Sıra, listedeki sırayla kaydedilir. Seri imzada yalnız sırası gelen imzacı
            imzalayabilir.
          </p>
        </div>
      )}
    </Modal>
  );
}
